import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import type {
  Account, PointAdjustment, PointAdjustmentResult, PointHistoryRow, Tier as CrmTier,
} from '../crmClient';
import { Callout, Toolbar } from '../../components';
import { ScreenSkeleton } from '../../components/ScreenSkeleton';
import {
  MemberScreen,
  type AdjustmentInput, type AdjustmentItem, type AdjustmentOutcome, type MemberDetail, type MemberSummary,
  type MemberTierStep, type PointHistoryItem, type PointKind,
} from '../../screens/MemberScreen';

/**
 * ต่อหน้าสมาชิกและคะแนนเข้ากับ CRM module — ปรับคะแนนด้วยมือ (FR-ADM-04)
 *
 * ⚠️ รายชื่อสมาชิกโหลดได้ครั้งละ 500 คน — backend ยังไม่มีการค้นหาที่ server
 *    ช่องค้นหาจึงกรองได้แค่ในรายชื่อที่โหลดมา (หน้าจอบอกผู้ใช้เมื่อโหลดมาไม่ครบ)
 * ⚠️ สลับสมาชิกเร็ว ๆ ได้ — คำตอบของคนก่อนหน้าที่มาช้าต้องถูกทิ้ง ไม่ให้ข้อมูลสองคนปนกัน
 */

const ACCOUNT_LIMIT = 500;
const HISTORY_PAGE = 50;

const num = (v: string | null | undefined) => (v ? Number(v) : 0);

const toSummary = (a: Account): MemberSummary => ({
  id: String(a.id),
  name: a.fullName,
  tierName: a.currentTier?.name ?? null,
  rewardPoints: num(a.rewardPoints),
});

const toDetail = (a: Account): MemberDetail => {
  const soonest = [...(a.nextExpireRewardPoints ?? [])].sort((x, y) => x.expiredAt.localeCompare(y.expiredAt))[0];
  return {
    id: String(a.id),
    name: a.fullName,
    rewardPoints: num(a.rewardPoints),
    tierPoints: num(a.tierPoints),
    tierName: a.currentTier?.name ?? null,
    nextTier: a.nextTier ? { name: a.nextTier.name, startPoint: num(a.nextTier.startPoint) } : null,
    expiringReward: soonest ? { points: num(soonest.totalAmount), at: soonest.expiredAt } : null,
  };
};

const referenceText = (r: PointHistoryRow): string | null => {
  if (!r.reference) return null;
  const n = r.reference.number;
  switch (r.reference.type) {
    case 'CLAIM': return n ? `คำขอสะสมคะแนน · เลขคำสั่งซื้อ ${n}` : `คำขอสะสมคะแนน #${r.reference.id}`;
    case 'REDEMPTION': return n ?? `การแลกของรางวัล #${r.reference.id}`;
    case 'ADJUSTMENT': return n ? `อ้างอิง ${n}` : null;
    default: return null;
  }
};

const toHistoryItem = (r: PointHistoryRow): PointHistoryItem => ({
  id: String(r.id),
  kind: r.pointType,
  source: r.source,
  amount: num(r.amount),
  at: r.createdAt,
  expiresAt: r.expiredAt,
  cancelled: r.isCancelled,
  referenceText: referenceText(r),
  byName: r.createdBy?.fullName ?? null,
  adjustment: r.adjustment
    ? { reasonCode: r.adjustment.reasonCode, reason: r.adjustment.reason, customerNote: r.adjustment.customerNote }
    : null,
});

const toAdjustmentItem = (a: PointAdjustment): AdjustmentItem => ({
  id: String(a.id),
  status: a.status,
  rewardAmount: num(a.rewardAmount),
  tierAmount: num(a.tierAmount),
  reasonCode: a.reasonCode,
  reason: a.reason,
  customerNote: a.customerNote,
  reference: a.reference,
  expiresAt: a.expiredAt,
  byName: a.createdBy?.fullName ?? '—',
  at: a.createdAt,
  cancelledByName: a.cancelledBy?.fullName ?? null,
  cancelledAt: a.cancelledAt,
  cancelReason: a.cancelReason,
});

const toOutcome = (r: PointAdjustmentResult | undefined): AdjustmentOutcome => ({
  isNew: r?.isNew ?? true,
  rewardBefore: num(r?.rewardPointsBefore),
  rewardAfter: num(r?.rewardPointsAfter),
  tierPointsBefore: num(r?.tierPointsBefore),
  tierPointsAfter: num(r?.tierPointsAfter),
  tierBefore: r?.tierBefore?.name ?? null,
  tierAfter: r?.tierAfter?.name ?? null,
  tierChanged: r?.tierChanged ?? false,
  expiredReward: num(r?.expiredRewardPoints),
  expiredTier: num(r?.expiredTierPoints),
});

/** หน้าจอเลือกวันสุดท้ายที่ใช้ได้ — คะแนนใช้ได้ถึงสิ้นวันนั้นตามเวลาไทย */
const endOfDay = (ymd: string) => `${ymd}T23:59:59+07:00`;

export function MemberContainer() {
  const { api, dataMode } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [tiers, setTiers] = useState<CrmTier[]>([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Account | null>(null);
  const [detailState, setDetailState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [history, setHistory] = useState<PointHistoryRow[]>([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  /**
   * ประวัติแสดงเฉพาะคะแนนแลกของ — ยอดใช้จ่ายสะสม (tier points) เกิดจากรายการเดียวกันทุกแถว
   * ถ้าแสดงทั้งสองชนิด ทุกรายการจะซ้ำสองบรรทัดและทำให้อ่านยาก
   */
  const historyKind: PointKind = 'REWARD';
  const [adjustments, setAdjustments] = useState<PointAdjustment[]>([]);

  /** นับรอบการโหลดของสมาชิกที่เลือก — คำตอบของรอบเก่าถูกทิ้ง */
  const run = useRef(0);

  useEffect(() => {
    if (isMock) { setState('ready'); return undefined; }
    let cancelled = false;
    setState('loading');
    Promise.all([api.listAccounts(ACCOUNT_LIMIT), api.listTiers()])
      .then(([page, tierPage]) => {
        if (cancelled) return;
        setAccounts(page.items);
        setTruncated(page.total > page.items.length);
        setTiers(tierPage.items);
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [isMock, api]);

  /** โหลดข้อมูลของสมาชิกหนึ่งคนใหม่ทั้งหมด — ใช้ตอนเลือก และหลังปรับ/ยกเลิก */
  const loadMember = useCallback(async (id: string, kind: PointKind | null, quiet = false) => {
    const mine = (run.current += 1);
    if (!quiet) setDetailState('loading');
    setHistoryLoading(true);
    try {
      const accountId = Number(id);
      const [account, page, adj] = await Promise.all([
        api.getAccount(accountId),
        api.listPointHistory(accountId, { pointType: kind ?? undefined, limit: HISTORY_PAGE, offset: 0 }),
        api.listPointAdjustments({ account: accountId }),
      ]);
      if (mine !== run.current) return;
      setDetail(account);
      setHistory(page.items);
      setHistoryHasMore(Boolean(page.next));
      setAdjustments(adj.items);
      /* ยอดในรายชื่อทางซ้ายต้องตรงกับรายละเอียด */
      setAccounts((prev) => prev.map((a) => (a.id === account.id ? account : a)));
      setDetailState('ready');
    } catch {
      if (mine === run.current) setDetailState('error');
    } finally {
      if (mine === run.current) setHistoryLoading(false);
    }
  }, [api]);

  const onSelect = useCallback((id: string) => {
    setSelectedId(id);
    setDetail(null);
    setHistory([]);
    setAdjustments([]);
    void loadMember(id, historyKind);
  }, [loadMember, historyKind]);

  const onLoadMoreHistory = useCallback(async () => {
    if (!selectedId) return;
    const mine = run.current;
    setHistoryLoading(true);
    try {
      const page = await api.listPointHistory(Number(selectedId), {
        pointType: historyKind ?? undefined, limit: HISTORY_PAGE, offset: history.length,
      });
      if (mine !== run.current) return;
      setHistory((prev) => [...prev, ...page.items]);
      setHistoryHasMore(Boolean(page.next));
    } finally {
      if (mine === run.current) setHistoryLoading(false);
    }
  }, [api, selectedId, historyKind, history.length]);

  const onAdjust = useCallback(async (input: AdjustmentInput): Promise<AdjustmentOutcome> => {
    if (!selectedId) throw new Error('ยังไม่ได้เลือกสมาชิก');
    const created = await api.createPointAdjustment({
      account: Number(selectedId),
      rewardAmount: input.rewardAmount.toFixed(2),
      tierAmount: input.tierAmount.toFixed(2),
      reasonCode: input.reasonCode,
      reason: input.reason,
      customerNote: input.customerNote,
      reference: input.reference,
      expiredAt: input.expiresOn ? endOfDay(input.expiresOn) : null,
      clientRequestId: input.requestId,
    });
    await loadMember(selectedId, historyKind, true);
    return toOutcome(created.result);
  }, [api, selectedId, historyKind, loadMember]);

  const onCancel = useCallback(async (adjustmentId: string, reason: string): Promise<AdjustmentOutcome> => {
    const cancelled = await api.cancelPointAdjustment(Number(adjustmentId), reason);
    if (selectedId) await loadMember(selectedId, historyKind, true);
    return toOutcome(cancelled.result);
  }, [api, selectedId, historyKind, loadMember]);

  const members = useMemo(() => accounts.map(toSummary), [accounts]);
  const tierSteps: MemberTierStep[] = useMemo(
    () => tiers.map((t) => ({ id: String(t.id), name: t.name, startPoint: num(t.startPoint) })),
    [tiers],
  );

  if (isMock) {
    return (
      <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
        <Toolbar title="สมาชิกและคะแนน" />
        <div className="p-[var(--space-4)]">
          <Callout tone="info" title="หน้านี้ใช้กับข้อมูลจริงเท่านั้น">
            การดูและปรับคะแนนของสมาชิกต้องต่อกับระบบจริง โหมดสาธิตไม่มีข้อมูลสมาชิก
          </Callout>
        </div>
      </div>
    );
  }
  if (state === 'loading') return <ScreenSkeleton title="สมาชิกและคะแนน" blocks={3} />;

  return (
    <MemberScreen
      initialState={state === 'error' ? 'error' : 'ready'}
      members={members}
      membersTruncated={truncated}
      tiers={tierSteps}
      selectedId={selectedId}
      onSelect={onSelect}
      detail={detail ? toDetail(detail) : null}
      detailState={detailState}
      history={history.map(toHistoryItem)}
      historyHasMore={historyHasMore}
      historyLoading={historyLoading}
      onLoadMoreHistory={() => { void onLoadMoreHistory(); }}
      adjustments={adjustments.map(toAdjustmentItem)}
      onAdjust={onAdjust}
      onCancel={onCancel}
    />
  );
}
